import React, { useEffect, useState } from "react";
import EntityApi from "../../state/entity-api";

export default function AlertControls({ fn, entityId }) {
  const [alertControls, setAlertControls] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    async function loadPrefs() {
      try {
        const res = await EntityApi.getEmailPreferences(entityId);
        if (res?.alertControl) {
          const controls = res.alertControl;
          setAlertControls(controls);

          const prefMap = {};
          controls.forEach((c) => {
            const enabled =
              !c.email_restrict || !c.whatsapp_restrict || !c.push_restrict;
            prefMap[c.key] = { enabled };
          });
          setPrefs(prefMap);
        }
      } catch (err) {
        console.error("Failed to load preferences", err);
      }
    }
    loadPrefs();
  }, [entityId]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? alertControls.filter((m) => m.label.toLowerCase().includes(q))
    : alertControls;

  // Toggle for single row
  function toggleAll(mKey) {
    setPrefs((p) => ({
      ...p,
      [mKey]: { enabled: !p[mKey]?.enabled },
    }));
  }

  // Select/Deselect All
  function setAll(enabled) {
    const next = { ...prefs };
    filtered.forEach((m) => (next[m.key] = { enabled }));
    setPrefs(next);
  }

  async function savePreferences() {
    setSaving(true);
    setMessage(null);
    try {
      const updated = alertControls.map((c) => ({
        ...c,
        email_restrict: !prefs[c.key]?.enabled,
        whatsapp_restrict: !prefs[c.key]?.enabled,
        push_restrict: !prefs[c.key]?.enabled,
      }));

      const res = await EntityApi.updateEmailPreferences({
        entityId,
        alertControl: updated,
      });

      setMessage({ type: res.type, text: res.message });
    } catch (err) {
      console.error(err);
      setMessage({ type: "error", text: "Failed to save preferences." });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  }

  return (
    <div className="box">
      <div className="level mb-4">
        <div className="level-left">
          <button className="button is-warning" onClick={() => fn(false)}>
            ← Back
          </button>
          <div className="ml-3">
            <h1 className="title is-4 mb-1">Notification Settings</h1>
          </div>
        </div>
      </div>

      <div className="field is-grouped is-align-items-center is-flex-wrap-wrap mb-4">
        <div className="control is-expanded">
          <input
            className="input"
            type="search"
            placeholder="Search templates..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="control">
          <button className="button is-link" onClick={() => setAll(true)}>
            Select All
          </button>
        </div>
        <div className="control">
          <button className="button is-link is-light" onClick={() => setAll(false)}>
            Deselect All
          </button>
        </div>

        <div className="control">
          <button
            className={`button is-primary ${saving ? "is-loading" : ""}`}
            onClick={savePreferences}
            disabled={saving}
          >
            Save Preferences
          </button>
        </div>
      </div>

      {message && (
        <div className="control">
          <div
            className={`notification py-2 px-3 is-light ${
              message.type === "success" ? "is-success" : "is-danger"
            }`}
            style={{ marginBottom: 0 }}
          >
            {message.text}
          </div>
        </div>
      )}

      <div className="table-container">
        <table className="table is-striped is-fullwidth is-hoverable">
          <thead>
            <tr>
              <th>Notification Template</th>
              <th className="has-text-centered">Notification Control</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((m) => (
              <tr key={m.key}>
                <td>{m.label}</td>
                <td className="has-text-centered">
                  <button
                    onClick={() => toggleAll(m.key)}
                    aria-pressed={prefs[m.key]?.enabled}
                    className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none"
                    style={{
                      backgroundColor: prefs[m.key]?.enabled
                        ? "#48c774"
                        : "#E0115F",
                      border: "none",
                    }}
                  >
                    <span
                      className="inline-block w-5 h-5 bg-white rounded-full shadow transition-transform"
                      style={{
                        transform: prefs[m.key]?.enabled
                          ? "translateX(1.25rem)"
                          : "translateX(0)",
                      }}
                    ></span>
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan="2" className="has-text-centered has-text-grey">
                  No templates available.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
